// src/views/Seguridad/Usuarios/ListaUsuario.jsx
import React, { useEffect, useState } from "react";
import IconEdit from "../../../components/icons/IconEdit";
import IconTrash from "../../../components/icons/IconTrash";

// Componentes reutilizables
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import DynamicTable from "../../../components/listing/DynamicTable";
import IconEye from "../../../components/icons/IconEye";

// Hooks y utilidades
import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

// Componentes de la vista
import FiltroUsuario from "./FiltroUsuario";
import EditarUsuario from "./EditarUsuario";
import VerUsuario from "./VerUsuario";
import EliminarUsuario from "./EliminarUsuario";
import CrearUsuarios from "./CrearUsuarios";

// debounce simple (copiado del original)
function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function ListaUsuario() {
  // === listado desde API ===
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // === fuerza refetch explícito (editar/crear/eliminar) ===
  const [reloadKey, setReloadKey] = useState(0);

  // === estados UI ===
  const [mostrarCrearUsuarios, setMostrarCrearUsuarios] = useState(false);
  useEscClose(() => setMostrarCrearUsuarios(false), mostrarCrearUsuarios);

  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  useEscClose(() => {
    setMostrarFiltros(false);
    document.activeElement?.blur();
  }, mostrarFiltros);

  const [filtros, setFiltros] = useState({});

  const [q, setQ] = useState("");
  const debouncedQ = useDebounced(q, 400);

  const [porPagina, setPorPagina] = useState(30);
  const [pagina, setPagina] = useState(1); // UI 1‑based

  const [showVerUsuario, setShowVerUsuario] = useState(false);
  const [codigoVerUsuario, setCodigoVerUsuario] = useState(null);
  useEscClose(() => {
    setShowVerUsuario(false);
    document.activeElement?.blur();
  }, showVerUsuario);

  const [mostrarEditor, setMostrarEditor] = useState(false);
  const [usuSel, setUsuSel] = useState(null);
  useEscClose(() => {
    setMostrarEditor(false);
    document.activeElement?.blur();
  }, mostrarEditor);

  const [mostrarEliminar, setMostrarEliminar] = useState(false);
  const [usuarioSel, setUsuarioSel] = useState(null);
  useEscClose(() => {
    setMostrarEliminar(false);
    document.activeElement?.blur();
  }, mostrarEliminar);

  const hasActiveFilters = (f) => {
    if (!f) return false;
    return Object.values(f).some((v) =>
      Array.isArray(v) ? v.length > 0 : (v ?? "") !== ""
    );
  };

  const limpiarSeleccion = () => {
    setFiltros({});
    setMostrarFiltros(false);
    setPagina(1);
  };

  // === fetch listado ===
  useEffect(() => {
    const abort = new AbortController();

    async function load() {
      setLoading(true);
      setErr("");
      try {
        const p0 = Math.max(0, pagina - 1); // backend 0‑based
        const qs = new URLSearchParams();
        qs.set("page", String(p0));
        qs.set("size", String(porPagina));

        // filtros aplicados
        if (filtros.codigo) qs.set("codigo", filtros.codigo);
        if (filtros.nombre) qs.set("nombre", filtros.nombre);
        if (filtros.estado) qs.set("estado", filtros.estado);
        if (filtros.clienteId) qs.set("clienteId", filtros.clienteId);
        if (filtros.eessId) qs.set("eessId", filtros.eessId);
        if (filtros.rolId) qs.set("rolId", filtros.rolId);

        // buscador rápido si no hay filtros
        if (!Object.keys(filtros).length && debouncedQ.trim() !== "") {
          qs.set("nombre", debouncedQ.trim());
        }

        const url = `${apiRoutes.usuarios.list}?${qs.toString()}`;
        const data = await appFetchJson(url, { signal: abort.signal });

        setItems(Array.isArray(data.items) ? data.items : []);
        setTotal(
          Number.isFinite(data.totalElements)
            ? data.totalElements
            : data.items?.length ?? 0
        );
        setTotalPaginas(Math.max(1, data.totalPages ?? 1));

        if (data.totalPages && pagina > data.totalPages && data.totalPages > 0) {
          setPagina(data.totalPages);
        }
      } catch (e) {
        if (e?.name !== "AbortError") {
          setErr(e?.message || "Error al cargar usuarios");
        }
      } finally {
        setLoading(false);
      }
    }

    load();
    return () => abort.abort();
  }, [pagina, porPagina, debouncedQ, filtros, reloadKey]);

  // índices mostrados (ya no se usan directamente, pero se mantienen por si acaso)
  const indiceInicio = total === 0 ? 0 : (pagina - 1) * porPagina + 1;
  const indiceFin = total === 0 ? 0 : Math.min(pagina * porPagina, total);

  const cambiarPagina = (num) => {
    if (num < 1) num = 1;
    if (num > totalPaginas) num = totalPaginas;
    setPagina(num);
  };

  useEffect(() => setPagina(1), [porPagina, debouncedQ]);

  // === vistas secundarias (Crear ocupa toda la pantalla) ===
  if (mostrarCrearUsuarios) {
    return (
      <CrearUsuarios
        onVolver={() => {
          setMostrarCrearUsuarios(false);
          setPagina(1);
          setReloadKey((k) => k + 1);
        }}
      />
    );
  }

  // ===========================
  //       COLUMNAS PARA TABLA
  // ===========================
  const columns = [
    { header: "Código", accessor: "codigo", type: "code", width: "100px" },
    { header: "Cliente", accessor: "clienteNombre", type: "text", width: "150px" },
    { header: "Nombre", accessor: "nombreUsuario", type: "text", width: "200px" },
    { header: "Rol", accessor: "rol", type: "text", width: "120px" },
    //Activo: verde, Inactivo: Rojo
    {
  header: "Estado",
  accessor: "estadoNombre",
  type: "status",
  width: "100px",
  render: (row) => {
    const estado = row.estadoNombre?.toUpperCase() || "";
    const esActivo = estado.includes('ACTIVO') && !estado.includes('INACTIVO');
    const textColor = esActivo ? 'text-green-600' : 'text-red-600';
    return <span className={`font-bold ${textColor}`}>{row.estadoNombre}</span>;
  }
},
    {
      header: "Acción",
      type: "action",
      width: "120px",
      render: (row) => (
        <div className="flex justify-center items-center gap-2">
          <button
            className="p-1 text-gray-500 hover:text-blue-600"
            title="Ver detalle"
            onClick={() => {
              setCodigoVerUsuario(row.id);
              setShowVerUsuario(true);
            }}
          >
            <IconEye/>
          </button>
          <button
            className="p-1 text-gray-500 hover:text-emerald-600"
            title="Editar"
            onClick={() => {
              setUsuSel(row);
              setMostrarEditor(true);
            }}
          >
            <IconEdit/>
          </button>
          <button
            className="p-1 text-gray-500 hover:text-red-600"
            title="Eliminar"
            onClick={() => {
              setUsuarioSel(row);
              setMostrarEliminar(true);
            }}
          >
            <IconTrash/>
          </button>
        </div>
      ),
    },
  ];

  // Función para determinar el color del borde izquierdo en móvil según el estado
const getRowBorderColor = (row) => {
  const estado = row.estadoNombre?.toUpperCase() || "";
  const esActivo = estado.includes('ACTIVO') && !estado.includes('INACTIVO');
  return esActivo ? 'border-green-600' : 'border-red-600';
};

  // ===========================
  //       RENDER PRINCIPAL
  // ===========================
  return (
    <div className="page">
      {/* HEADER con filtros, botón agregar y selector de página */}
      <ListHeader
        title="👤 Usuarios"
        filters={filtros}
        onFilterClick={() => setMostrarFiltros(true)}
        onClearFilters={limpiarSeleccion}
        showAdd
        addLabel="Agregar"
        onAddClick={() => setMostrarCrearUsuarios(true)}
        pageSize={porPagina}
        onPageSizeChange={(size) => setPorPagina(size)}
      />

      {/* DRAWER DE FILTROS */}
      {mostrarFiltros && (
        <FiltroUsuario
          initialFilters={filtros}
          onSubmit={(applied) => {
            setFiltros(applied);
            setMostrarFiltros(false);
            setPagina(1);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {/* INDICADOR DE CARGA / ERROR */}
      {loading && (
        <div className="mt-4 text-sm text-center text-gray-600">
          Cargando usuarios…
        </div>
      )}
      {!loading && err && (
        <div className="mt-4 text-sm text-center text-red-600">{err}</div>
      )}

      {/* TABLA DINÁMICA */}
      {!loading && !err && (
        <DynamicTable
          size="L" // 6 columnas
          data={items}
          columns={columns}
          getRowColor={getRowBorderColor}
        />
      )}

      {/* PAGINADOR */}
      <ListFooter
        page={pagina}
        pageSize={porPagina}
        total={total}
        onPageChange={cambiarPagina}
      />

      {/* MODALES */}
      {showVerUsuario && (
        <VerUsuario
          id={codigoVerUsuario}
          onClose={() => setShowVerUsuario(false)}
        />
      )}

      {mostrarEditor && usuSel && (
        <EditarUsuario
          userId={usuSel.id}
          onVolver={() => {
            setMostrarEditor(false);
            setUsuSel(null);
          }}
          onSaved={() => {
            setReloadKey((k) => k + 1);
            setMostrarEditor(false);
            setUsuSel(null);
          }}
        />
      )}

      {mostrarEliminar && usuarioSel && (
        <EliminarUsuario
          initial={{
            id: usuarioSel.id,
            codigo: usuarioSel.codigo,
            nombre: usuarioSel.nombreUsuario,
            estado: usuarioSel.estadoNombre,
            asignadoA: [],
          }}
          onVolver={() => {
            setMostrarEliminar(false);
            setUsuarioSel(null);
          }}
          onEliminar={() => {
            setReloadKey((k) => k + 1);
            setMostrarEliminar(false);
            setUsuarioSel(null);
          }}
        />
      )}
    </div>
  );
}